import { createSlice } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

const initialState = {
  callStatus: null,
  callType: null,
  dialSec: null,
  callId: null,
  callEnd: false,
  breakType: null,
  callNumber: null,
  screenType: '',
  isDialNumber: null,
  isReDial: false,
  breakDetails: null,
  currentCallDetail: null,
  holdTime: 0,
  holdStartTime: null,
  activeCallName: null,
  sipStatus: 'Connecting',
  conferenceDetail: null,
  chatBotHistory: []
};

const dialSlice = createSlice({
  name: 'dial',
  initialState,
  reducers: {
    onSetCallStatus(state, action) {
      state.callStatus = action.payload;
    },
    onSetCallType(state, action) {
      state.callType = action.payload;
    },
    setCallDialSec(state, action) {
      state.dialSec = action.payload;
    },
    setCallId(state, action) {
      state.callId = action.payload;
    },
    setActiveCallName(state, action) {
      state.activeCallName = action.payload;
    },
    setCallNumber(state, action) {
      state.callNumber = action.payload;
    },
    setScreenType(state, action) {
      state.screenType = action.payload;
    },
    setCallEnd(state, action) {
      state.callEnd = action.payload;
    },
    setIsDialNumber(state, action) {
      state.isDialNumber = action.payload;
    },
    setIsReDial(state, action) {
      state.isReDial = action.payload;
    },
    setSelectBreakType(state, action) {
      state.breakType = action.payload;
    },
    setBreakDetails(state, action) {
      state.breakDetails = action.payload;
    },
    setCurrentCallDetail(state, action) {
      state.currentCallDetail = action.payload;
    },
    setHoldTime(state, action) {
      state.holdTime = action.payload;
    },
    setHoldStartTime(state, action) {
      state.holdStartTime = action.payload;
    },
    setSipStatus(state, action) {
      state.sipStatus = action.payload;
    },
    setConferenceDetail(state, action) {
      state.conferenceDetail = action.payload;
    },
    setChatBotHistory(state, action) {
      state.chatBotHistory = action.payload;
    },
    clearDialSlice: () => initialState
  },
  extraReducers: (builder) => {}
});

export default dialSlice.reducer;
export const {
  clearDialSlice,
  onSetCallStatus,
  onSetCallType,
  setCallDialSec,
  setCallId,
  setActiveCallName,
  setCallNumber,
  setCallEnd,
  setSelectBreakType,
  setScreenType,
  setIsDialNumber,
  setIsReDial,
  setBreakDetails,
  setCurrentCallDetail,
  setHoldTime,
  setHoldStartTime,
  setSipStatus,
  setConferenceDetail,
  setChatBotHistory
} = dialSlice.actions;

export const selectCallStatus = (state) => state.dial.callStatus;
export const useCallingStatus = () => {
  const callStatus = useSelector(selectCallStatus);
  return useMemo(() => callStatus, [callStatus]);
};

export const selectCallType = (state) => state.dial.callType;
export const useCallType = () => {
  const callType = useSelector(selectCallType);
  return useMemo(() => callType, [callType]);
};

export const selectDialSec = (state) => state.dial.dialSec;
export const useDialSec = () => {
  const dialSec = useSelector(selectDialSec);
  return useMemo(() => dialSec, [dialSec]);
};

export const selectCallId = (state) => state.dial.callId;
export const useCallId = () => {
  const callId = useSelector(selectCallId);
  return useMemo(() => callId, [callId]);
};

export const selectCallEnd = (state) => state.dial.callEnd;
export const useCallEnd = () => {
  const callEnd = useSelector(selectCallEnd);
  return useMemo(() => callEnd, [callEnd]);
};

export const selectBreakType = (state) => state.dial.breakType;
export const useBreakType = () => {
  const breakType = useSelector(selectBreakType);
  return useMemo(() => breakType, [breakType]);
};

export const selectCallNumber = (state) => state.dial.callNumber;
export const useCallNumber = () => {
  const callNumber = useSelector(selectCallNumber);
  return useMemo(() => callNumber, [callNumber]);
};

export const selectScreenType = (state) => state.dial.screenType;
export const useScreenType = () => {
  const screenType = useSelector(selectScreenType);
  return useMemo(() => screenType, [screenType]);
};

export const selectIsDialNumber = (state) => state.dial.isDialNumber;
export const useIsDialNumber = () => {
  const isDialNumber = useSelector(selectIsDialNumber);
  return useMemo(() => isDialNumber, [isDialNumber]);
};

export const selectIsReDial = (state) => state.dial.isReDial;
export const useIsReDial = () => {
  const isReDial = useSelector(selectIsReDial);
  return useMemo(() => isReDial, [isReDial]);
};

export const selectBreakDetails = (state) => state.dial.breakDetails;
export const useBreakDetails = () => {
  const breakDetails = useSelector(selectBreakDetails);
  return useMemo(() => breakDetails, [breakDetails]);
};

export const selectCurrentCallDetail = (state) => state.dial.currentCallDetail;
export const useCurrentCallDetail = () => {
  const currentCallDetail = useSelector(selectCurrentCallDetail);
  return useMemo(() => currentCallDetail, [currentCallDetail]);
};

export const selectHoldTime = (state) => state.dial.holdTime;
export const useHoldTime = () => {
  const holdTime = useSelector(selectHoldTime);
  return useMemo(() => holdTime, [holdTime]);
};

export const selectHoldStartTime = (state) => state.dial.holdStartTime;
export const useHoldStartTime = () => {
  const holdStartTime = useSelector(selectHoldStartTime);
  return useMemo(() => holdStartTime, [holdStartTime]);
};

export const selectActiveCallName = (state) => state.dial.activeCallName;
export const usesActiveCallName = () => {
  const activeCallName = useSelector(selectActiveCallName);
  return useMemo(() => activeCallName, [activeCallName]);
};

export const selectSipStatus = (state) => state.dial.sipStatus;
export const useSipStatus = () => {
  const sipStatus = useSelector(selectSipStatus);
  return useMemo(() => sipStatus, [sipStatus]);
};

export const selectConferenceDetail = (state) => state.dial.conferenceDetail;
export const useConferenceDetail = () => {
  const conferenceDetail = useSelector(selectConferenceDetail);
  return useMemo(() => conferenceDetail, [conferenceDetail]);
};

export const selectChatBotHistory = (state) => state.dial.chatBotHistory;
export const useChatBotHistory = () => {
  const chatBotHistory = useSelector(selectChatBotHistory);
  return useMemo(() => chatBotHistory, [chatBotHistory]);
};
